/**
 * Charts, drawn as inline SVG.
 *
 * Rules held to throughout, and each is a way a dashboard goes wrong:
 *
 * - **One axis, never two.** Two measures at different scales are two
 *   charts, not one chart with a second y-axis.
 * - **Horizontal bars for names.** Vendor names are long; rotating
 *   labels to fit a vertical bar chart makes them unreadable to save
 *   space that a horizontal layout has anyway.
 * - **Recessive grid, thin marks, direct labels.** The value sits at the
 *   end of its bar rather than in a legend nobody reads back to.
 * - **Text never wears the series colour.** Labels stay in ink; the mark
 *   beside them carries identity.
 * - **A table alternative ships with every chart**, because a chart is
 *   not readable by a screen reader and a number somebody needs to copy
 *   should be copyable.
 *
 * The palette is validated: lightness band, chroma floor, colour-vision
 * separation and contrast against the surface all pass.
 */

import type { ReactNode } from "react";

import { groupDigits } from "./format";

export const SERIES = ["#2563eb", "#c2410c", "#0891b2", "#7c3aed"] as const;
export const STATUS = { ok: "#16a34a", warn: "#d97706", danger: "#dc2626" } as const;

/** Short money for an axis: 1 234 567 becomes 1.2M. */
function compact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return groupDigits(String(Math.round(value)));
}

export interface Datum {
  label: string;
  value: number;
  count: number;
  /** Overrides the series colour, for status-coloured charts. */
  color?: string;
  /** What to print at the end of the bar, when it is not the value. */
  display?: string;
}

export function ChartCard({
  title,
  subtitle,
  children,
  data,
  valueHeader,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  data: Datum[];
  valueHeader: string;
}) {
  return (
    <section className="card chart">
      <div className="card-head">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      <div className="chart-body">
        {data.length === 0 ? (
          <p className="note" style={{ margin: 0 }}>
            No data for these filters yet.
          </p>
        ) : (
          <>
            {children}
            <details className="table-alt">
              <summary>Show as a table</summary>
              <table>
                <thead>
                  <tr>
                    <th>{title}</th>
                    <th>{valueHeader}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td>{row.display ?? groupDigits(String(row.value))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Horizontal bars, sorted by the caller.
 *
 * Labels sit outside the plot on the left and values at the end of each
 * bar, so nothing has to be looked up and no label is rotated.
 */
export function BarsH({
  data,
  labelWidth = 132,
  formatValue,
}: {
  data: Datum[];
  labelWidth?: number;
  formatValue?: (value: number) => string;
}) {
  const width = 480;
  const rowHeight = 30;
  const height = data.length * rowHeight + 8;
  const max = Math.max(...data.map((d) => d.value), 0) || 1;
  const plot = width - labelWidth - 62;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Bar chart">
      {data.map((row, index) => {
        const y = index * rowHeight + 4;
        const barWidth = Math.max(2, (row.value / max) * plot);
        const value = row.display ?? (formatValue ? formatValue(row.value) : compact(row.value));
        return (
          <g key={row.label}>
            <text
              className="cat-label"
              x={labelWidth - 10}
              y={y + 15}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {row.label.length > 20 ? `${row.label.slice(0, 19)}…` : row.label}
            </text>
            <rect
              className="bar"
              x={labelWidth}
              y={y + 4}
              width={barWidth}
              height={16}
              rx={4}
              fill={row.color ?? SERIES[0]}
            >
              <title>
                {row.label}: {value} ({row.count} document{row.count === 1 ? "" : "s"})
              </title>
            </rect>
            <text
              className="value-label"
              x={labelWidth + barWidth + 8}
              y={y + 15}
              dominantBaseline="middle"
            >
              {value}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Vertical bars over time.
 *
 * Time reads left to right, so months are the one thing that does not
 * become a horizontal bar chart here.
 */
export function BarsV({ data, currency }: { data: Datum[]; currency: string | null }) {
  const width = 480;
  const height = 210;
  const padLeft = 46;
  const padBottom = 30;
  const max = Math.max(...data.map((d) => d.value), 0) || 1;
  const plotWidth = width - padLeft - 10;
  const plotHeight = height - padBottom - 12;
  const slot = plotWidth / Math.max(data.length, 1);
  const barWidth = Math.min(38, slot * 0.62);
  const ticks = [0, 0.5, 1];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Bar chart over time">
      <g className="grid">
        {ticks.map((t) => {
          const y = 12 + plotHeight - t * plotHeight;
          return <line key={t} x1={padLeft} x2={width - 10} y1={y} y2={y} />;
        })}
      </g>
      <g className="axis">
        {ticks.map((t) => {
          const y = 12 + plotHeight - t * plotHeight;
          return (
            <text key={t} x={padLeft - 8} y={y} textAnchor="end" dominantBaseline="middle">
              {compact(max * t)}
            </text>
          );
        })}
      </g>

      {data.map((row, index) => {
        const barHeight = Math.max(2, (row.value / max) * plotHeight);
        const x = padLeft + index * slot + (slot - barWidth) / 2;
        const y = 12 + plotHeight - barHeight;
        return (
          <g key={row.label}>
            <rect
              className="bar"
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx={4}
              fill={SERIES[0]}
            >
              <title>
                {row.label}: {compact(row.value)} {currency ?? ""} ({row.count} documents)
              </title>
            </rect>
            <text
              className="axis"
              x={x + barWidth / 2}
              y={height - 10}
              textAnchor="middle"
              fill="var(--muted)"
              fontSize={10}
              fontFamily="var(--mono)"
            >
              {row.label.slice(2)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
